import { useQuery } from "@apollo/client";
import { useMemo, useCallback } from "react";
import { LOCATIONS } from "@/api/queries/locations";
import { LocationCity } from "@/__generated__/graphql";

export const useLocations = (filter: string) => {
  const { data, loading, error, fetchMore } = useQuery(LOCATIONS, {
    variables: {
      filter: filter,
      first: 20,
      after: null,
    },
    skip: !filter,
    notifyOnNetworkStatusChange: true,
  });

  const { locations, totalCount, pageInfo } = useMemo(() => {
    if (!data?.locationCities) {
      return {
        locations: [],
        totalCount: 0,
        pageInfo: {
          endCursor: null,
          hasNextPage: false,
        },
      };
    }

    const locationCities = data.locationCities;
    const locationsArray =
      locationCities.edges
        ?.map((edge) => edge?.node)
        .filter(
          (node): node is LocationCity => node !== null && node !== undefined,
        ) || [];

    return {
      locations: locationsArray,
      totalCount: locationCities.totalCount,
      pageInfo: {
        endCursor: locationCities.pageInfo?.endCursor,
        hasNextPage: locationCities.pageInfo?.hasNextPage || false,
      },
    };
  }, [data]);

  const loadMore = useCallback(() => {
    if (pageInfo.hasNextPage && pageInfo.endCursor && !loading) {
      fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            locationCities: {
              ...fetchMoreResult.locationCities,
              edges: [
                ...(prev.locationCities?.edges || []),
                ...(fetchMoreResult.locationCities?.edges || []),
              ],
            },
          };
        },
      });
    }
  }, [pageInfo, loading, fetchMore]);

  return {
    locations,
    loading,
    error,
    totalCount,
    pageInfo,
    loadMore,
  };
};
